/**
 * =============================================================================
 * IDS CRM Web - The table of notification kinds, one tick per way of sending
 * =============================================================================
 *
 * Settings -> Notifications (Gareth, 14 October 2026). The one place where
 * notifications are set: a person's own Email and Teams ticks, and (admins)
 * the company's starting Email and Teams ticks with a third column for what
 * is posted to the Teams channels. The kinds, their groups and names come from
 * the server (api config/notificationKinds.js), so a new kind appears here by
 * itself.
 * =============================================================================
 */

import React from 'react';
import './NotificationTicks.css';

const EMAIL_AND_TEAMS = [{ key: 'email', label: 'Email' }, { key: 'teams', label: 'Teams' }];

/**
 * @param {object} props
 * @param {Array<{key: string, label: string, hint?: string}>} props.groups
 * @param {Array<{key: string, group: string, label: string}>} props.kinds
 * @param {Object<string, Object<string, boolean>>} props.ticks by kind, then by column key
 * @param {(kind: string, column: string, value: boolean) => void} props.onChange
 * @param {Array<{key: string, label: string, off?: boolean}>} [props.columns] Email and Teams unless given;
 *        a column that is off is shown but locked
 * @param {boolean} [props.emailOff] email cannot be sent at all: its ticks are locked
 * @param {boolean} [props.teamsOff] Teams is not set up: its ticks are locked
 * @param {boolean} [props.disabled]
 * @param {string} props.idPrefix keeps the ids of the two tables on one page apart
 */
function NotificationTicks({
  groups, kinds, ticks, onChange, columns = null, emailOff = false, teamsOff = false, disabled = false, idPrefix
}) {
  const shown = (columns || EMAIL_AND_TEAMS).map(column => ({
    ...column,
    off: Boolean(column.off) || (column.key === 'email' && emailOff) || (column.key === 'teams' && teamsOff)
  }));
  const grid = { gridTemplateColumns: `minmax(0, 1fr) repeat(${shown.length}, 72px)` };
  return (
    <div className="notification-ticks">
      {groups.map((group) => {
        const rows = kinds.filter(kind => kind.group === group.key);
        if (rows.length === 0) return null;
        const groupId = `${idPrefix}-${group.key}`;
        return (
          <section key={group.key} className="notification-ticks-group" aria-labelledby={groupId}>
            <div className="notification-ticks-head" style={grid}>
              <h4 id={groupId}>{group.label}</h4>
              {shown.map(column => <span key={column.key} className="notification-ticks-channel">{column.label}</span>)}
            </div>
            {group.hint && <p className="notification-ticks-hint">{group.hint}</p>}
            {rows.map((kind) => {
              const tick = ticks[kind.key] || {};
              return (
                <div key={kind.key} className="notification-ticks-row" style={grid}>
                  <span className="notification-ticks-label">{kind.label}</span>
                  {shown.map(column => (
                    <label key={column.key} className="notification-ticks-box">
                      <input
                        type="checkbox"
                        aria-label={`${column.label}: ${kind.label}`}
                        checked={Boolean(tick[column.key])}
                        disabled={disabled || column.off}
                        onChange={event => onChange(kind.key, column.key, event.target.checked)}
                      />
                    </label>
                  ))}
                </div>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}

export default NotificationTicks;
